/**
 * Page-scoped browser (OS) notifications. Mirrors `sound-utils.ts`: a single
 * `showBrowserNotification()` entry point with a per-tag debounce, plus thin
 * permission helpers. No service worker — notifications fire only while a tab
 * is open (including when backgrounded), matching how the sound alerts behave.
 */

// Served from /public; a 192px icon renders well in the OS notification.
const ICON_URL = "/favicon-192.png";

// Debounce tracking — stops a burst of same-tag arrivals from thrashing.
const DEBOUNCE_MS = 500;
const lastShownByTag: Record<string, number> = {};

export const isBrowserNotificationSupported = (): boolean =>
  typeof window !== "undefined" && "Notification" in window;

export const getNotificationPermission = (): NotificationPermission =>
  isBrowserNotificationSupported() ? Notification.permission : "denied";

/**
 * Requests OS notification permission. Must be called from a user gesture (e.g.
 * a settings toggle click) or browsers silently ignore it. Resolves to the
 * resulting permission state ("denied" if the API is unavailable).
 */
export const requestNotificationPermission =
  async (): Promise<NotificationPermission> => {
    if (!isBrowserNotificationSupported()) return "denied";
    try {
      return await Notification.requestPermission();
    } catch {
      return getNotificationPermission();
    }
  };

export interface BrowserNotificationOptions {
  title: string;
  body?: string;
  /** Coalescing key — a notification with the same tag replaces the prior one. */
  tag?: string;
}

/**
 * Shows an OS notification when permission is granted. Debounced per tag (500ms)
 * so rapid same-watchlist matches don't thrash; the tag also coalesces visually
 * (same tag replaces rather than stacks). Clicking focuses the app tab.
 */
export const showBrowserNotification = ({
  title,
  body,
  tag,
}: BrowserNotificationOptions): void => {
  if (getNotificationPermission() !== "granted") return;

  const debounceKey = tag ?? title;
  const now = Date.now();
  if (now - (lastShownByTag[debounceKey] ?? 0) < DEBOUNCE_MS) return;
  lastShownByTag[debounceKey] = now;

  try {
    const notification = new Notification(title, {
      body,
      tag,
      icon: ICON_URL,
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch (error) {
    console.error("Error showing browser notification:", error);
  }
};
